import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { Palmtree, Mail, ArrowRight, ChevronLeft } from "lucide-react";
import { getSiteProfile } from "../lib/site-profile";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const { login } = useAuth();
  const siteProfile = getSiteProfile();
  const [step, setStep] = useState("email"); // email | otp
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoOtp, setDemoOtp] = useState("");

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/otp/send`, { email });
      setDemoOtp(response.data.demo_otp); // For demo purposes
      setStep("otp");
      toast.success("OTP sent to your email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the complete OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/otp/verify`,
        { email, otp_code: otp },
        { withCredentials: true }
      );
      login(response.data.user);
      toast.success(siteProfile.welcomeToast);
      navigate(redirectTo.startsWith("/") ? redirectTo : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{ 
          backgroundImage: "url(https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200)" 
        }}
      >
        <div className="h-full w-full bg-black/30 backdrop-blur-sm flex items-end p-12">
          <div className="text-white">
            <h2 className="font-serif font-bold text-4xl mb-4">
              Discover Paradise
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              Your AI-powered Caribbean concierge. From sunrise beaches to midnight adventures.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
            <ChevronLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Palmtree className="w-7 h-7 text-white" />
            </div>
            <span className="font-serif font-bold text-2xl">{siteProfile.brandName}</span>
          </div>

          {step === "email" ? (
            <>
              <h1 className="font-serif font-bold text-3xl mb-2">Welcome</h1>
              <p className="text-muted-foreground mb-8">
                Sign in with your email to get started
              </p>

              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 h-12 rounded-xl"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-xl btn-primary"
                  disabled={loading}
                  data-testid="send-otp-btn"
                >
                  {loading ? "Sending..." : "Continue with Email"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-background px-4 text-muted-foreground">or</span>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full h-12 rounded-xl"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </>
          ) : (
            <>
              <h1 className="font-serif font-bold text-3xl mb-2">Enter OTP</h1>
              <p className="text-muted-foreground mb-8">
                We sent a 6-digit code to {email}
              </p>

              {/* Demo OTP Display */}
              {demoOtp && (
                <div className="bg-secondary/10 rounded-xl p-4 mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Demo OTP (for testing):</p>
                  <p className="font-mono text-2xl font-bold text-secondary">{demoOtp}</p>
                </div>
              )}

              <div className="mb-6">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  data-testid="otp-input"
                >
                  <InputOTPGroup className="gap-2 justify-center w-full">
                    <InputOTPSlot index={0} className="w-12 h-14 text-xl rounded-xl" />
                    <InputOTPSlot index={1} className="w-12 h-14 text-xl rounded-xl" />
                    <InputOTPSlot index={2} className="w-12 h-14 text-xl rounded-xl" />
                    <InputOTPSlot index={3} className="w-12 h-14 text-xl rounded-xl" />
                    <InputOTPSlot index={4} className="w-12 h-14 text-xl rounded-xl" />
                    <InputOTPSlot index={5} className="w-12 h-14 text-xl rounded-xl" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button 
                className="w-full h-12 rounded-xl btn-primary"
                onClick={handleVerifyOtp}
                disabled={loading || otp.length !== 6}
                data-testid="verify-otp-btn"
              >
                {loading ? "Verifying..." : "Verify & Sign In"}
              </Button>

              <Button 
                variant="ghost" 
                className="w-full mt-4"
                onClick={() => setStep("email")}
                data-testid="change-email-btn"
              >
                Use a different email
              </Button>
            </>
          )}

          <p className="text-center text-sm text-muted-foreground mt-8">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
